/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Interactive Connection Wire
   Individual interactive SVG cubic Bézier connection.
   Preserves per-edge hit testing, selection, hover, and deletion.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { EdgeId, PortType } from '../../domain/types';
import styles from './ConnectionWire.module.css';

interface ConnectionWireProps {
  id: EdgeId;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  sourceType: PortType;
  selected: boolean;
  onSelect: (edgeId: EdgeId, additive: boolean) => void;
  onDelete: (edgeId: EdgeId) => void;
}

const PORT_COLOR_MAP: Record<PortType, string> = {
  color: '#e8b4f8',
  number: '#a9e1f7',
  image: '#8fe46c',
  mask: '#f8d84e',
  text: '#f0f0ee',
  boolean: '#63d9db',
  any: '#9b9b97',
};

export const ConnectionWire: React.FC<ConnectionWireProps> = ({
  id,
  x1,
  y1,
  x2,
  y2,
  sourceType,
  selected,
  onSelect,
  onDelete,
}) => {
  // Cubic Bézier control points
  const dx = Math.max(40, Math.abs(x2 - x1) * 0.5);
  const pathData = `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`;

  const strokeColor = PORT_COLOR_MAP[sourceType] || '#9b9b97';

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(id, e.shiftKey || e.metaKey);
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(id);
  };

  return (
    <g
      className={`${styles.wireGroup} ${selected ? styles.selected : ''}`}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
    >
      {/* Invisible wide hit area for effortless clicking and hover */}
      <path d={pathData} className={styles.hitArea} />
      {/* Visible styled wire */}
      <path
        d={pathData}
        className={styles.visibleWire}
        stroke={strokeColor}
      />
    </g>
  );
};
